import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { X, Shield, Cookie, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_PREFERENCES_KEY = 'cookie_preferences';

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
    
    // Reload page to apply tracking preferences
    window.location.reload();
  };

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    savePreferences(necessaryOnly);
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  // Cookie consent banner is temporarily hidden
  return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-500">
        <Card className="max-w-2xl mx-auto bg-background/95 backdrop-blur-sm border shadow-lg">
          <div className="p-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-sm text-muted-foreground">
                We use cookies to improve your experience.{' '}
                <a href="/privacy-policy" className="text-primary hover:underline">
                  Learn more about our privacy policy
                </a>
              </p>
              <div className="flex gap-2 flex-shrink-0">
                <Button 
                  onClick={acceptAll}
                  size="sm"
                >
                  Accept
                </Button>
                <Button 
                  onClick={acceptNecessary}
                  variant="outline"
                  size="sm"
                >
                  Reject
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Cookie className="w-5 h-5" />
              Cookie Preferences
            </DialogTitle>
            <DialogDescription>
              Manage your cookie preferences. You can enable or disable different types of cookies below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Necessary Cookies */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">Necessary Cookies</h4>
                    <Badge variant="secondary" className="text-xs">Required</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Essential for the website to function properly. These cannot be disabled.
                  </p>
                </div>
                <Switch checked={true} disabled />
              </div>
              <div className="pl-4 text-xs text-muted-foreground">
                <p>• Session management</p>
                <p>• Security features</p>
                <p>• Basic functionality</p>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="space-y-3 pt-3 border-t">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h4 className="font-semibold">Analytics Cookies</h4>
                  <p className="text-sm text-muted-foreground">
                    Help us understand how visitors interact with our website by collecting anonymous information.
                  </p>
                </div>
                <Switch
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, analytics: checked })
                  }
                />
              </div>
              <div className="pl-4 text-xs text-muted-foreground">
                <p>• Page views and navigation patterns</p>
                <p>• Time spent on pages</p>
                <p>• Error tracking</p>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="space-y-3 pt-3 border-t">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h4 className="font-semibold">Marketing Cookies</h4>
                  <p className="text-sm text-muted-foreground">
                    Used to track visitors across websites to display relevant advertisements and measure campaign performance.
                  </p>
                </div>
                <Switch
                  checked={preferences.marketing}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, marketing: checked })
                  }
                />
              </div>
              <div className="pl-4 text-xs text-muted-foreground">
                <p>• Google Ads tracking</p>
                <p>• Facebook Pixel</p>
                <p>• Campaign attribution</p>
              </div>
            </div>
          </div>

          <div className="flex justify-between gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={acceptNecessary}>
                Reject All
              </Button>
              <Button onClick={saveCustomPreferences}>
                Save Preferences
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Hook to check if user has consented to specific cookie types
export const useCookieConsent = () => {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (savedPrefs) {
      setPreferences(JSON.parse(savedPrefs));
    }
  }, []);

  return preferences;
};

// Utility function to check if tracking is allowed
export const isTrackingAllowed = (): boolean => {
  const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (!savedPrefs) return false;
  
  const prefs: CookiePreferences = JSON.parse(savedPrefs);
  return prefs.marketing || prefs.analytics;
};

// Utility to check specific tracking type
export const hasConsent = (type: 'analytics' | 'marketing'): boolean => {
  const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (!savedPrefs) return false;
  
  const prefs: CookiePreferences = JSON.parse(savedPrefs);
  return prefs[type];
};
